import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  FaLink,
  FaEdit,
  FaSave,
  FaTimes,
  FaGavel,
  FaUser,
  FaBuilding,
  FaCalendarAlt,
  FaExclamationTriangle,
  FaCheckCircle,
  FaFileAlt,
  FaPlus,
  FaSync
} from 'react-icons/fa';
import './BondDetail.css';

const BondDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [bond, setBond] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('bond');
  const [editMode, setEditMode] = useState(false);
  const [editedBond, setEditedBond] = useState({});
  const [syncing, setSyncing] = useState(false);
  const [editingCourtDateId, setEditingCourtDateId] = useState(null);
  const [editedCourtDate, setEditedCourtDate] = useState({});
  const [jails, setJails] = useState([]);

  useEffect(() => {
    fetchBondDetails();
    fetchJails();
  }, [id]);

  const fetchBondDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/bonds/${id}`);
      if (response.data.success) {
        setBond(response.data.data);
        setEditedBond(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching bond details:', error);
      alert('Failed to load bond details');
    } finally {
      setLoading(false);
    }
  };

  const fetchJails = async () => {
    try {
      const response = await axios.get('/jails');
      if (response.data.success) {
        setJails(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching jails:', error);
    }
  };

  const handleEdit = () => {
    setEditMode(true);
    setEditedBond({ ...bond });
  };

  const handleCancel = () => {
    setEditMode(false);
    setEditedBond({ ...bond });
  };

  const handleSave = async () => {
    try {
      const response = await axios.put(`/bonds/${id}`, {
        bondNumber: editedBond.bond_number,
        bondAmount: editedBond.bond_amount,
        premiumAmount: editedBond.premium_amount,
        caseNumber: editedBond.case_number,
        charges: editedBond.charges,
        arrestDate: editedBond.arrest_date,
        bookingNumber: editedBond.booking_number,
        arrestingAgency: editedBond.arresting_agency,
        detentionFacilityId: editedBond.detention_facility_id,
        status: editedBond.status,
        courtId: editedBond.court_id,
        judgeName: editedBond.judge_name,
        prosecutorName: editedBond.prosecutor_name,
        defenseAttorney: editedBond.defense_attorney,
        courtCaseStatus: editedBond.court_case_status,
        courtFilingDate: editedBond.court_filing_date,
        defendantPhone: editedBond.defendant_phone,
        notes: editedBond.notes
      });

      if (response.data.success) {
        alert('Bond updated successfully');
        setEditMode(false);
        fetchBondDetails();
      }
    } catch (error) {
      console.error('Error updating bond:', error);
      alert('Failed to update bond');
    }
  };

  const handleSyncFullCourt = async () => {
    if (!bond.case_number) {
      alert('Bond must have a case number to sync with FullCourt');
      return;
    }

    if (!bond.court_name) {
      alert('Bond must have an assigned court to sync with FullCourt');
      return;
    }

    const confirmSync = window.confirm(
      `This will sync bond with FullCourt using:\n\n` +
      `Case Number: ${bond.case_number}\n` +
      `Court: ${bond.court_name}\n\n` +
      `The scraper will open a visible browser window so you can monitor the process. Continue?`
    );

    if (!confirmSync) return;

    try {
      setSyncing(true);
      const response = await axios.post(`/bonds/${id}/sync-fullcourt`);

      if (response.data.success) {
        const scrapedData = response.data.data.scraped;

        // Show scraped data to user
        const message = `Successfully synced with FullCourt!\n\n` +
          `Case Number: ${scrapedData.caseNumber || 'N/A'}\n` +
          `Defendant: ${scrapedData.defendantName || 'N/A'}\n` +
          `Judge: ${scrapedData.judgeName || 'N/A'}\n` +
          `Case Status: ${scrapedData.caseStatus || 'N/A'}\n` +
          `Charges Found: ${scrapedData.charges?.length || 0}\n` +
          `Hearings Found: ${scrapedData.hearings?.length || 0}\n\n` +
          `Check the console for detailed scraped data.`;

        alert(message);
        console.log('FullCourt Scraped Data:', scrapedData);

        // Refresh bond details
        fetchBondDetails();
      }
    } catch (error) {
      console.error('Error syncing with FullCourt:', error);
      const errorMsg = error.response?.data?.message || 'Failed to sync with FullCourt';
      alert(`Sync Error: ${errorMsg}`);
    } finally {
      setSyncing(false);
    }
  };

  const handleInputChange = (field, value) => {
    setEditedBond(prev => ({ ...prev, [field]: value }));
  };

  const handleEditCourtDate = (courtDate) => {
    setEditingCourtDateId(courtDate.id);
    setEditedCourtDate({ ...courtDate });
  };

  const handleCancelCourtDateEdit = () => {
    setEditingCourtDateId(null);
    setEditedCourtDate({});
  };

  const handleCourtDateChange = (field, value) => {
    setEditedCourtDate(prev => ({ ...prev, [field]: value }));
  };

  const handleSaveCourtDate = async () => {
    try {
      const response = await axios.put(`/court-dates/${editingCourtDateId}`, {
        hearingType: editedCourtDate.hearing_type,
        scheduledDate: editedCourtDate.scheduled_date,
        scheduledTime: editedCourtDate.scheduled_time,
        appearanceType: editedCourtDate.appearance_type,
        judge: editedCourtDate.judge,
        clientNotified: editedCourtDate.client_notified,
        notes: editedCourtDate.notes
      });

      if (response.data.success) {
        alert('Court date updated successfully');
        setEditingCourtDateId(null);
        setEditedCourtDate({});
        fetchBondDetails();
      }
    } catch (error) {
      console.error('Error updating court date:', error);
      alert('Failed to update court date');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      active: 'badge-active',
      pending: 'badge-pending',
      completed: 'badge-completed',
      forfeited: 'badge-forfeited',
      exonerated: 'badge-completed',
      revoked: 'badge-forfeited'
    };
    return statusMap[status?.toLowerCase()] || 'badge-pending';
  };

  if (loading) {
    return (
      <div className="page-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading bond details...</p>
        </div>
      </div>
    );
  }

  if (!bond) {
    return (
      <div className="page-container">
        <div className="error-message">
          <FaExclamationTriangle />
          <h2>Bond Not Found</h2>
          <button onClick={() => navigate('/bonds')} className="btn btn-primary">
            Back to Bonds
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bond-detail-container">
      {/* Header */}
      <div className="bond-detail-header">
        <div className="header-left">
          <h1>
            <FaLink /> Bond Details
          </h1>
          <div className="bond-status-badges">
            <span className="status-label">Bond Status:</span>
            <span className={`badge ${getStatusBadgeClass(bond.status)}`}>
              {bond.status?.toUpperCase()}
            </span>
          </div>
        </div>
        <div className="header-actions">
          {!editMode ? (
            <>
              <button className="btn btn-secondary" onClick={handleEdit}>
                <FaEdit /> Edit Bond
              </button>
              {process.env.NODE_ENV !== 'production' && (
                <button
                  className="btn btn-primary"
                  onClick={handleSyncFullCourt}
                  disabled={syncing || !bond.case_number || !bond.court_name}
                  title={
                    !bond.case_number
                      ? 'Case number required'
                      : !bond.court_name
                      ? 'Court assignment required'
                      : 'Sync with FullCourt Montana'
                  }
                >
                  <FaSync className={syncing ? 'fa-spin' : ''} />{' '}
                  {syncing ? 'Syncing...' : 'Sync with FullCourt'}
                </button>
              )}
              <button className="btn btn-primary" onClick={() => alert('Update Status - Coming Soon')}>
                Update Bond Status
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-secondary" onClick={handleCancel}>
                <FaTimes /> Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSave}>
                <FaSave /> Save Changes
              </button>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="bond-tabs">
        <button
          className={`tab-button ${activeTab === 'bond' ? 'active' : ''}`}
          onClick={() => setActiveTab('bond')}
        >
          <FaFileAlt /> Bond
        </button>
        <button
          className={`tab-button ${activeTab === 'payments' ? 'active' : ''}`}
          onClick={() => setActiveTab('payments')}
        >
          Payments
        </button>
        <button
          className={`tab-button ${activeTab === 'contracts' ? 'active' : ''}`}
          onClick={() => setActiveTab('contracts')}
        >
          Contracts
        </button>
        <button
          className={`tab-button ${activeTab === 'documents' ? 'active' : ''}`}
          onClick={() => setActiveTab('documents')}
        >
          Bond Documents
        </button>
        <button
          className={`tab-button ${activeTab === 'notes' ? 'active' : ''}`}
          onClick={() => setActiveTab('notes')}
        >
          Notes
        </button>
        <button
          className={`tab-button ${activeTab === 'indemnitors' ? 'active' : ''}`}
          onClick={() => setActiveTab('indemnitors')}
        >
          Bond Indemnitors
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === 'bond' && (
          <>
            {/* Bond and Power Certificate Information */}
            <div className="info-section">
              <div className="section-header">
                <h2>BOND & POWER CERTIFICATE INFORMATION</h2>
                {!editMode && (
                  <button className="btn-icon-edit" onClick={handleEdit}>
                    <FaEdit />
                  </button>
                )}
              </div>

              <div className="info-grid">
                {/* Row 1: Power Number, Surety, Power Receipt Uploaded */}
                <div className="info-row">
                  <label>Power Number:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.bond_number || ''}
                      onChange={(e) => handleInputChange('bond_number', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className="highlight-value">{bond.bond_number || 'N/A'}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>Surety/Insurance:</label>
                  <span>Allegheny Casualty</span>
                </div>

                <div className="info-row">
                  <label>Power Receipt Uploaded:</label>
                  <span className="badge badge-forfeited">NO</span>
                </div>

                {/* Row 2: Bond Amount, Premium Amount, Premium Paid Today */}
                <div className="info-row">
                  <label>Bond Amount:</label>
                  <span className="amount-value">{formatCurrency(bond.bond_amount)}</span>
                </div>

                <div className="info-row">
                  <label>Premium Amount:</label>
                  <span className="amount-value">{formatCurrency(bond.premium_amount)}</span>
                </div>

                <div className="info-row">
                  <label>Premium Paid Today:</label>
                  <span className="amount-value">{formatCurrency(bond.premium_paid || 0)}</span>
                </div>

                {/* Row 3: Created By, Created Date and Time */}
                <div className="info-row">
                  <label>Created By:</label>
                  <span>{bond.created_by_name || bond.agent_name || 'System'}</span>
                </div>

                <div className="info-row">
                  <label>Created Date & Time:</label>
                  <span>{formatDateTime(bond.created_at)}</span>
                </div>

                <div className="info-row"></div>

                {/* Row 4: Posting Agent, Office, Posted Date and Time */}
                <div className="info-row">
                  <label>Posting Agent:</label>
                  <span>{bond.agent_name || 'N/A'}</span>
                </div>

                <div className="info-row">
                  <label>Office:</label>
                  <span>Main Office</span>
                </div>

                <div className="info-row">
                  <label>Posted Date & Time:</label>
                  <span>{formatDateTime(bond.issue_date)}</span>
                </div>
              </div>
            </div>

            {/* Arresting Agency & Booking Information */}
            <div className="info-section">
              <div className="section-header">
                <h2>ARRESTING AGENCY & BOOKING INFORMATION</h2>
              </div>

              <div className="info-grid">
                <div className="info-row">
                  <label>Arresting Agency:</label>
                  {editMode ? (
                    <select
                      value={editedBond.arresting_agency || ''}
                      onChange={(e) => handleInputChange('arresting_agency', e.target.value)}
                      className="edit-input"
                    >
                      <option value="">Select Agency</option>
                      <option value="Sheriff">Sheriff</option>
                      <option value="City Police">City Police</option>
                      <option value="Highway Patrol">Highway Patrol</option>
                    </select>
                  ) : (
                    <span>{bond.arresting_agency || 'N/A'}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>Booking Number:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.booking_number || ''}
                      onChange={(e) => handleInputChange('booking_number', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{bond.booking_number || 'N/A'}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>Arrest Date:</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedBond.arrest_date || ''}
                      onChange={(e) => handleInputChange('arrest_date', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{formatDate(bond.arrest_date)}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>Detention Facility:</label>
                  {editMode ? (
                    <select
                      value={editedBond.detention_facility_id || ''}
                      onChange={(e) => handleInputChange('detention_facility_id', e.target.value)}
                      className="edit-input"
                    >
                      <option value="">Select Detention Facility</option>
                      {jails.map(jail => (
                        <option key={jail.id} value={jail.id}>
                          {jail.name} - {jail.county}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <span>{bond.detention_facility_name || 'N/A'}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Court & Case Information */}
            <div className="info-section">
              <div className="section-header">
                <h2>COURT & CASE INFORMATION</h2>
              </div>

              <div className="info-grid">
                <div className="info-row">
                  <label>Court:</label>
                  <span>
                    {bond.court_name && !bond.court_name.includes('Detention') && !bond.court_name.includes('Jail') ? (
                      <Link to={`/address-book/courts/${bond.court_id}`} className="link-primary">
                        {bond.court_name}
                      </Link>
                    ) : (
                      'TBD'
                    )}
                  </span>
                </div>

                <div className="info-row">
                  <label>County:</label>
                  <span>{bond.county || 'N/A'}</span>
                </div>

                <div className="info-row">
                  <label>Case Number:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.case_number || ''}
                      onChange={(e) => handleInputChange('case_number', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className="highlight-value">{bond.case_number || 'TBD'}</span>
                  )}
                </div>

                <div className="info-row">
                  <label>Judge:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.judge_name || ''}
                      onChange={(e) => handleInputChange('judge_name', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className={bond.judge_name ? 'highlight-value' : ''}>
                      {bond.judge_id && bond.court_id ? (
                        <Link to={`/address-book/courts/${bond.court_id}`} className="link-primary">
                          {bond.judge_name}
                        </Link>
                      ) : (
                        bond.judge_name || 'N/A'
                      )}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>Prosecutor:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.prosecutor_name || ''}
                      onChange={(e) => handleInputChange('prosecutor_name', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className={bond.prosecutor_name ? 'highlight-value' : ''}>
                      {bond.prosecutor_id && bond.county ? (
                        <Link to={`/address-book/county/${bond.county}`} className="link-primary" title={`View ${bond.county} County Attorney's Office`}>
                          {bond.prosecutor_name}
                        </Link>
                      ) : (
                        bond.prosecutor_name || 'N/A'
                      )}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>Defense Attorney:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.defense_attorney || ''}
                      onChange={(e) => handleInputChange('defense_attorney', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className={bond.defense_attorney ? 'highlight-value' : ''}>
                      {bond.defense_attorney_id && bond.county ? (
                        <Link to={`/address-book/county/${bond.county}`} className="link-primary" title={`View ${bond.county} County Contacts`}>
                          {bond.defense_attorney}
                        </Link>
                      ) : (
                        bond.defense_attorney || 'N/A'
                      )}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>Court Case Status:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.court_case_status || ''}
                      onChange={(e) => handleInputChange('court_case_status', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span className={bond.court_case_status ? '' : ''}>
                      {bond.court_case_status || 'N/A'}
                    </span>
                  )}
                </div>

                <div className="info-row">
                  <label>Filing Date:</label>
                  {editMode ? (
                    <input
                      type="date"
                      value={editedBond.court_filing_date || ''}
                      onChange={(e) => handleInputChange('court_filing_date', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>
                      {bond.court_filing_date
                        ? new Date(bond.court_filing_date).toLocaleDateString()
                        : 'N/A'}
                    </span>
                  )}
                </div>

                {bond.fullcourt_synced_at && (
                  <div className="info-row">
                    <label>Last Synced:</label>
                    <span className="text-muted" style={{ fontSize: '0.9em' }}>
                      {new Date(bond.fullcourt_synced_at).toLocaleString()}
                    </span>
                  </div>
                )}

                <div className="info-row full-width">
                  <label>Charges:</label>
                  {editMode ? (
                    <textarea
                      value={editedBond.charges || ''}
                      onChange={(e) => handleInputChange('charges', e.target.value)}
                      className="edit-textarea"
                      rows="3"
                    />
                  ) : (
                    <div>
                      {Array.isArray(bond.charges) && bond.charges.length > 0 ? (
                        bond.charges.map((charge, idx) => (
                          <div key={charge.id || idx} style={{ marginBottom: '8px' }}>
                            <strong>Count {charge.count || idx + 1}:</strong> {charge.description || 'N/A'}
                            {charge.mca_code && <div style={{ fontSize: '0.9em', color: '#666' }}>MCA: {charge.mca_code}</div>}
                          </div>
                        ))
                      ) : typeof bond.charges === 'string' ? (
                        <span>{bond.charges}</span>
                      ) : (
                        <span>N/A</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Defendant Information */}
            <div className="info-section">
              <div className="section-header">
                <h2>DEFENDANT INFORMATION</h2>
              </div>
              <div className="defendant-info">
                <div className="info-row">
                  <label>Name:</label>
                  <Link to={`/defendants/${bond.defendant_id}`} className="link-primary defendant-link">
                    <FaUser /> {bond.defendant_name}
                  </Link>
                </div>
                <div className="info-row">
                  <label>Phone:</label>
                  {editMode ? (
                    <input
                      type="text"
                      value={editedBond.defendant_phone || ''}
                      onChange={(e) => handleInputChange('defendant_phone', e.target.value)}
                      className="edit-input"
                    />
                  ) : (
                    <span>{bond.defendant_phone || 'N/A'}</span>
                  )}
                </div>
              </div>
            </div>

            {/* Court Dates Section */}
            <div className="court-dates-section">
              <div className="section-header">
                <h2>COURT DATES</h2>
                <button className="btn btn-primary btn-sm" onClick={() => alert('Add Court Date - Coming Soon')}>
                  <FaPlus /> ADD COURT DATE
                </button>
              </div>

              {bond.fullcourt_synced_at && (
                <div style={{ padding: '10px 20px', fontSize: '0.9em', color: '#666', borderBottom: '1px solid #e0e0e0' }}>
                  <strong>Last Synced from FullCourt:</strong> {new Date(bond.fullcourt_synced_at).toLocaleString()}
                </div>
              )}

              <table className="court-dates-table">
                <thead>
                  <tr>
                    <th>Hearing Type</th>
                    <th>Dates</th>
                    <th>Court Appearance</th>
                    <th>Judge</th>
                    <th>Client Notified</th>
                    <th>Comments</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {bond.court_dates && bond.court_dates.length > 0 ? (
                    bond.court_dates.map((courtDate) => (
                      <tr key={courtDate.id}>
                        {editingCourtDateId === courtDate.id ? (
                          <>
                            <td>
                              <input
                                type="text"
                                value={editedCourtDate.hearing_type || ''}
                                onChange={(e) => handleCourtDateChange('hearing_type', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <input
                                type="date"
                                value={editedCourtDate.scheduled_date || ''}
                                onChange={(e) => handleCourtDateChange('scheduled_date', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%', marginBottom: '4px' }}
                              />
                              <input
                                type="time"
                                value={editedCourtDate.scheduled_time || ''}
                                onChange={(e) => handleCourtDateChange('scheduled_time', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={editedCourtDate.appearance_type || ''}
                                onChange={(e) => handleCourtDateChange('appearance_type', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={editedCourtDate.judge || ''}
                                onChange={(e) => handleCourtDateChange('judge', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={editedCourtDate.client_notified || false}
                                onChange={(e) => handleCourtDateChange('client_notified', e.target.checked)}
                              />
                            </td>
                            <td>
                              <input
                                type="text"
                                value={editedCourtDate.notes || ''}
                                onChange={(e) => handleCourtDateChange('notes', e.target.value)}
                                className="edit-input"
                                style={{ width: '100%' }}
                              />
                            </td>
                            <td>
                              <button className="btn-icon" title="Save" onClick={handleSaveCourtDate}>
                                <FaSave />
                              </button>
                              <button className="btn-icon" title="Cancel" onClick={handleCancelCourtDateEdit}>
                                <FaTimes />
                              </button>
                            </td>
                          </>
                        ) : (
                          <>
                            <td>{courtDate.hearing_type}</td>
                            <td>
                              {formatDate(courtDate.scheduled_date)}
                              <br />
                              {courtDate.scheduled_time || ''}
                            </td>
                            <td>{courtDate.appearance_type || 'In Person'}</td>
                            <td>{courtDate.judge || 'N/A'}</td>
                            <td className="text-center">
                              <input
                                type="checkbox"
                                checked={courtDate.client_notified}
                                readOnly
                              />
                            </td>
                            <td>{courtDate.notes || ''}</td>
                            <td>
                              <button className="btn-icon" title="Edit" onClick={() => handleEditCourtDate(courtDate)}>
                                <FaEdit />
                              </button>
                            </td>
                          </>
                        )}
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="no-data">
                        No court dates scheduled. Click "ADD COURT DATE" to add one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}

        {activeTab === 'payments' && (
          <div className="tab-panel">
            <h3>Payment History</h3>
            <p className="coming-soon">Payment tracking coming soon...</p>
          </div>
        )}

        {activeTab === 'contracts' && (
          <div className="tab-panel">
            <h3>Contracts</h3>
            <p className="coming-soon">Contract management coming soon...</p>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className="tab-panel">
            <h3>Bond Documents</h3>
            <p className="coming-soon">Document management coming soon...</p>
          </div>
        )}

        {activeTab === 'notes' && (
          <div className="tab-panel">
            <h3>Notes</h3>
            <div className="notes-section">
              {editMode ? (
                <textarea
                  value={editedBond.notes || ''}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="notes-textarea"
                  rows="10"
                  placeholder="Enter notes about this bond..."
                />
              ) : (
                <p>{bond.notes || 'No notes available.'}</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'indemnitors' && (
          <div className="tab-panel">
            <h3>Bond Indemnitors / Co-Signers</h3>
            <p className="coming-soon">Indemnitor management coming soon...</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default BondDetail;
